import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";

import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { getSettings, updateSettings } from "api/settings.js";

import getLogger from "utils/logger.util";
import { userProfileStrings, valueStrings } from "constants/constantStrings";

import Dropdown from "components/Dropdown";
import DupontButton from "components/DupontButton";
import InputBox from "components/InputBox";
import PageLoading from "components/PageLoading";

import "./index.scss";

const languageOptions = ["English"];
const unitOptions = ["US"];

const AccountSettings = () => {
  const Logger = getLogger("AccountSettings");
  const [accountSettings, setAccountSettings] = useState({});
  const [orgAccountSettings, setOrgAccountSettings] = useState({});
  const [editStarted, setIseditedStarted] = useState(false);
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => fetchData(), []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getSettings();
      if (response.status >= 200 && response.status < 300) {
        const { Phone, Email, TimeZone, Units } = response.data;
        const languageText = "English";
        const settings = { Email, Phone, TimeZone, Units, Language: languageText };
        setAccountSettings(settings);
        setOrgAccountSettings(settings);
      }
    } catch (err) {
      Logger.error("fetchData - Error: ", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDropDownChange = (key, value) => {
    const settings = { ...accountSettings, [key]: value };
    setAccountSettings(settings);
    setConfirmDisabled(!value);
  };

  const onMobileChange = phoneNumber => {
    objAppInsights.trackEvent("mobileNumberChange");
    const { Phone } = accountSettings;
    if (Phone !== phoneNumber) {
      const settings = { ...accountSettings, Phone: phoneNumber };
      setAccountSettings(settings);
      setIseditedStarted(true);
      setConfirmDisabled(!phoneNumber);
    }
  };

  const handleSubmit = async () => {
    objAppInsights.trackEvent("accountSettingsSubmit");
    const { Phone } = accountSettings;
    if (Phone) {
      setConfirmDisabled(true);
      setIseditedStarted(false);
      const payload = { Phone: Phone };
      try {
        await updateSettings(payload);
        setOrgAccountSettings(accountSettings);
      } catch (err) {
        setConfirmDisabled(false);
        setIseditedStarted(true);
      }
    }
  };

  const handleReset = () => {
    setAccountSettings(orgAccountSettings);
    setIseditedStarted(false);
    setConfirmDisabled(true);
  };

  return (
    <div className="app-wrapper">
      {isLoading && <PageLoading />}
      <div className="headerTealText">{userProfileStrings.accountSetting}</div>
      <div className="descriptionText margin-top-16">{userProfileStrings.accountSettingMessage}</div>

      <InputBox
        name="account-settings-email"
        value={accountSettings.Email}
        customClass="textFieldWidth300 account-settings__email margin-top-16"
        labelText={userProfileStrings.emailID}
        disabled
      />

      <div className="bodyMainHeadingText margin-top-16 color-medium-grey">
        {userProfileStrings.addNotificationMobile}&nbsp;
        <span className="mobile-info">({userProfileStrings.prefixCountryCode})</span>
      </div>
      <PhoneInput
        placeholder={userProfileStrings.addNotificationMobilePlaceHolder}
        inputClass="mobile-input textFieldWidth300 account-settings__mobile-input height32"
        value={accountSettings.Phone}
        onChange={onMobileChange}
      />

      <div className="headerTealText margin-top-50">{userProfileStrings.accountPreferences}</div>

      <Dropdown
        disabled
        customClass="margin-top-16 textFieldWidth300"
        placeholder={valueStrings.chooseOptionStr}
        labelText={userProfileStrings.language}
        value={accountSettings.Language}
        options={languageOptions}
        name="acnt-pref-language-dropdown"
        onChange={value => handleDropDownChange(userProfileStrings.language, value)}
      />

      <Dropdown
        disabled
        customClass="margin-top-16 textFieldWidth300"
        placeholder={valueStrings.chooseOptionStr}
        labelText={userProfileStrings.units}
        value={accountSettings.Units}
        name="acnt-pref-units-dropdown"
        options={unitOptions}
        onChange={value => handleDropDownChange(userProfileStrings.units, value)}
      />

      <div className="flex-between  margin-top-16 width-36">
        <DupontButton id="acnt-pref-reset-btn" type="secondary" disabled={!editStarted} onClick={handleReset}>
          {userProfileStrings.reset}
        </DupontButton>
        <DupontButton id="acnt-pref-save-btn" type="primary" disabled={confirmDisabled} onClick={handleSubmit} customClass="save-button-width">
          {userProfileStrings.save}
        </DupontButton>
      </div>
    </div>
  );
};

export default AccountSettings;
